import { Box, Chip, List, ListItem, ListItemText, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { NotificationEntity } from '@/types/api';

const PRIORITY_COLOR: Record<string, 'error' | 'warning' | 'info'> = {
  critical: 'error',
  warning: 'warning',
  info: 'info',
};

// createdAt is epoch ms — format as "30s", "12m", "4h", "2d"
const formatAge = (createdAt: number): string => {
  const secs = Math.floor((Date.now() - createdAt) / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
};

interface NotificationListProps {
  notifications: NotificationEntity[];
  maxItems?: number;
}

export const NotificationList = ({ notifications, maxItems }: NotificationListProps) => {
  const { t } = useTranslation();

  const displayed = maxItems ? notifications.slice(0, maxItems) : notifications;

  if (displayed.length === 0) {
    return (
      <Box px={2} py={3} textAlign="center">
        <Typography variant="body2" color="text.secondary">
          {t('notifications.list.empty')}
        </Typography>
      </Box>
    );
  }

  return (
    <List disablePadding>
      {displayed.map((n) => (
        <ListItem
          key={n.id}
          divider
          alignItems="flex-start"
          sx={{ flexDirection: 'column', gap: 0.5, py: 1.5 }}
        >
          <Box display="flex" alignItems="center" gap={1} width="100%">
            <Chip
              label={t(`notifications.priority.${n.priority}`)}
              color={PRIORITY_COLOR[n.priority]}
              size="small"
              variant="outlined"
            />
            <ListItemText
              primary={n.title}
              slotProps={{ primary: { variant: 'body2', style: { fontWeight: 600 } } }}
              sx={{ m: 0 }}
            />
            <Typography
              variant="caption"
              color="text.disabled"
              flexShrink={0}
              title={new Date(n.createdAt).toLocaleString()}
            >
              {formatAge(n.createdAt)}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {n.summary}
          </Typography>
        </ListItem>
      ))}
    </List>
  );
};
